import { AdaStatLink } from "@/components/adastat-link";
import { type Column, DataList } from "@/components/data-list";
import { PlainValue } from "@/components/plain-value";
import type { DisplayAsset, DisplayUtxo } from "@/generated/api/model";
import { LOVELACE } from "@/lib/cardanoscan";
import { utxoRef } from "@/lib/utxo-ref";

const LOVELACE_PER_ADA = 1_000_000;

const formatAda = (lovelace: number) => {
    const whole = Math.floor(lovelace / LOVELACE_PER_ADA);
    const fraction = String(lovelace % LOVELACE_PER_ADA)
        .padStart(6, "0")
        .replace(/0+$/, "");
    return fraction ? `${whole}.${fraction}` : String(whole);
};

const tokensOf = (utxo: DisplayUtxo) => utxo.assets.filter((asset) => asset.id !== LOVELACE);

// One line per token, by unit id: that is what the Pairs lookup takes.
function Tokens({ tokens }: { tokens: DisplayAsset[] }) {
    if (tokens.length === 0) return <span className="text-muted-foreground">ADA only</span>;
    return (
        <ul className="space-y-1">
            {tokens.map((asset) => (
                <li key={asset.id} className="flex min-w-0 flex-wrap items-baseline gap-x-1.5">
                    {/* Wrapped on a phone card, one truncated line in the desktop table. */}
                    <PlainValue
                        value={asset.id}
                        label="Asset id"
                        truncated
                        className="min-w-0 font-mono text-xs break-all md:max-w-64 md:truncate md:break-normal"
                    />
                    <PlainValue
                        value={asset.quantity}
                        label="Asset quantity"
                        className="tabular-nums whitespace-nowrap"
                    />
                </li>
            ))}
        </ul>
    );
}

// The bot sends a datum as JSON (or a JSON string); show and copy it as one compact string.
const datumText = (datum: unknown) =>
    datum === null || datum === undefined ? null : typeof datum === "string" ? datum : JSON.stringify(datum);

const COLUMNS: Column<DisplayUtxo>[] = [
    {
        label: "Transaction",
        render: (utxo) => <AdaStatLink kind="transaction" value={utxo.tx_id} label="Transaction" fit="table" />,
        wide: true,
    },
    {
        label: "Address",
        render: (utxo) => <AdaStatLink kind="address" value={utxo.address} label="Address" fit="table" />,
        wide: true,
    },
    { label: "Index", value: (utxo) => utxo.index },
    { label: "Slot", value: (utxo) => utxo.block_slot, className: "tabular-nums" },
    { label: "ADA", value: (utxo) => formatAda(utxo.assets.find((asset) => asset.id === LOVELACE)?.quantity ?? 0) },
    {
        label: "Block",
        render: (utxo) => <AdaStatLink kind="block" value={utxo.block_id} label="Block" fit="table" />,
        wide: true,
    },
    { label: "Assets", render: (utxo) => <Tokens tokens={tokensOf(utxo)} />, wide: true },
    {
        label: "Datum",
        value: (utxo) => datumText(utxo.datum),
        className: "font-mono text-xs",
        wide: true,
        truncate: true,
    },
];

export function UtxoTable({ utxos, label }: { utxos: DisplayUtxo[]; label: string }) {
    return <DataList rows={utxos} columns={COLUMNS} rowKey={(utxo) => utxoRef(utxo.tx_id, utxo.index)} label={label} />;
}
